import { FC, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { scrollBarStyles } from '@/utils/theme';

import { Template } from '..';

const Dropdown = styled.div`
  position: relative;
`;

const Arrow = styled.svg<{ $opened: boolean }>`
  color: ${({ theme }) => theme.colors.gray};
  transition: transform 0.15s ease-out;

  ${({ $opened }) =>
    $opened &&
    css`
      transform: rotate(180deg);
    `}
`;

const Label = styled(Text)`
  flex: 1;
  padding: 0 8px;
  text-align: left;
`;

const Button = styled.button.attrs({ type: 'button' })<{ $opened: boolean }>`
  display: flex;
  align-items: center;
  width: 220px;
  max-width: 100%;
  height: 32px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $opened }) => theme.colors[$opened ? 'gray' : 'lightGray']};
  padding: 0 12px;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const Options = styled.div`
  position: absolute;
  box-shadow: 0px 0px 8px 0px #2222221f;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  z-index: 2;
  padding: 4px;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  ${scrollBarStyles};
  ${Button} {
    border: 0;
    height: auto;
    padding-top: 12px;
    padding-bottom: 12px;
    &:hover {
      background-color: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
`;

const SearchInput = styled(Input)`
  border: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  font-size: 12px;
  outline: none !important;
`;

interface Props {
  templates: Template[];
  activeTemplate: Template;
}

const TemplateSelector: FC<Props> = ({ templates, activeTemplate }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [search, setSearch] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const onSelect = (template: Template) => {
    const path = `/general/cover-images/generate/${template.slug}`;

    navigate(path, {
      replace: true,
    });

    setSearch('');
    setIsOpen(false);
  };

  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setSearch('');
  });

  const filteredTemplates = useMemo(() => {
    if (!search) {
      return templates;
    }

    if (!templates) {
      return null;
    }

    return templates.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, templates]);

  return (
    <Dropdown>
      <Button
        $opened={isOpen}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        {!isOpen ? (
          <>
            <Label $type="smallHeadline" as="span">
              {activeTemplate.name}
            </Label>
            <Arrow
              $opened={isOpen}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 10.5L12 5.5H4L8 10.5Z" fill="currentColor" />
            </Arrow>
          </>
        ) : (
          <SearchInput
            placeholder="Search by name"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </Button>
      {isOpen && (
        <Options ref={dropdownRef}>
          {filteredTemplates?.map((template) => (
            <Button
              onClick={() => onSelect(template)}
              key={template.slug}
              $opened={false}
            >
              <Label $type="smallHeadline" as="span">
                {template.name}
              </Label>
            </Button>
          ))}
        </Options>
      )}
    </Dropdown>
  );
};

export default TemplateSelector;
